import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  Plus,
  Edit3,
  Trash2,
  Play,
  Pause,
  Star,
  Megaphone,
  CheckCircle2,
  Clock,
  Ticket,
  RefreshCw,
} from 'lucide-react';
import { adminApi, formatNumber, formatDate, extractErrorMessage } from '@kardume/services';
import { theme } from '@kardume/theme';
import type {
  PromotionBenefitRequestDto,
  PromotionDto,
  PromotionRequestDto,
  PromotionStatus,
  PromotionType,
  Tone,
} from '@kardume/types';
import {
  PROMOTION_GALLERY_ERROR_MESSAGES,
  PROMOTION_GALLERY_MAX_COUNT,
  PROMOTION_STATUS_LABELS,
  PROMOTION_TYPE_LABELS,
} from '@kardume/types';
import {
  PageHeader,
  StatCard,
  Button,
  Badge,
  Table,
  TableContainer,
  ActionButton,
  SearchInput,
  Select,
  TextInput,
  RichTextInput,
  Switch,
  Modal,
  ConfirmModal,
  Pagination,
  ImageUpload,
  GalleryInput,
  Thumbnail,
} from '@kardume/ui';
import { useNotification } from '@kardume/hooks';
import { useAsync } from '../utils/useAsync';
import { useDebouncedValue } from '../utils/useDebouncedValue';
import { uploadMerchantImage } from '../utils/imageUpload';
import DataState from '../components/DataState';
import PromotionRulesInput, {
  hasPromotionRuleErrors,
  promotionRulesToDraft,
  promotionRulesToPayload,
  validatePromotionRules,
} from '../components/PromotionRulesInput';
import type { PromotionRuleDraft } from '../components/PromotionRulesInput';
import BenefitInput, { benefitToDraft } from '../components/BenefitInput';

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];
const DEFAULT_PAGE_SIZE = 20;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;
  margin-bottom: 1.25rem;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const Toolbar = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
  align-items: center;

  > *:first-child {
    flex: 1;
    min-width: 240px;
  }
`;

const Actions = styled.div`
  display: flex;
  gap: 0.4rem;
  justify-content: flex-end;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.9rem;
`;

const FullRow = styled.div`
  grid-column: 1 / -1;
`;

const SwitchRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  font-size: 0.85rem;
  color: ${(p) => p.theme.textSecondary};
`;

const FieldGroupLabel = styled.span`
  display: block;
  font-size: 0.75rem;
  font-weight: 600;
  color: ${(p) => p.theme.textSecondary};
  margin-bottom: 0.35rem;
`;

const FieldGroupHint = styled.p`
  font-size: 0.78rem;
  color: ${(p) => p.theme.textMuted};
  margin: 0 0 0.6rem;
`;

/** Map a promotion status to a Badge tone. */
const statusTone = (status: PromotionStatus): Tone => {
  switch (status) {
    case 'ACTIVE':
      return 'success';
    case 'DRAFT':
    case 'PENDING_REVIEW':
    case 'PAUSED':
      return 'warning';
    case 'REJECTED':
    case 'EXPIRED':
      return 'danger';
    default:
      return 'secondary';
  }
};

const TYPE_OPTIONS = (Object.keys(PROMOTION_TYPE_LABELS) as PromotionType[]).map((value) => ({
  value,
  label: PROMOTION_TYPE_LABELS[value],
}));

const STATUS_FILTER_OPTIONS = [
  { value: '', label: 'Todos os status' },
  ...(Object.keys(PROMOTION_STATUS_LABELS) as PromotionStatus[]).map((value) => ({
    value,
    label: PROMOTION_STATUS_LABELS[value],
  })),
];

const emptyForm: PromotionRequestDto = {
  title: '',
  description: '',
  type: 'DISCOUNT',
  discountPercent: undefined,
  cashbackPercent: undefined,
  imageUrl: undefined,
  highlighted: false,
  startsAt: '',
  endsAt: '',
  maxRedemptions: undefined,
  galleryUrls: [],
};

const PromotionsPage: React.FC = () => {
  const { showNotification } = useNotification();

  // Busca e filtro de status são resolvidos no servidor
  // (GET /v1/merchant/promotions aceita query, status e Pageable).
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const debouncedSearch = useDebouncedValue(search.trim(), 350);

  const { data, loading, error, reload } = useAsync(
    () =>
      adminApi.listPromotions({
        page,
        size: pageSize,
        query: debouncedSearch || undefined,
        status: statusFilter ? (statusFilter as PromotionStatus) : undefined,
      }),
    [page, pageSize, debouncedSearch, statusFilter]
  );

  // Indicadores globais (não apenas da página) - GET .../promotions/summary.
  const summaryState = useAsync(() => adminApi.getPromotionSummary(), []);

  // Catálogo de cláusulas mantido pela operação. Carregado uma vez por página e
  // não a cada abertura do modal: é uma lista pequena, que muda raramente, e
  // recarregá-la a cada clique em "Novo anúncio" só atrasaria o formulário.
  const ruleTemplatesState = useAsync(() => adminApi.listPromotionRuleTemplates(), []);

  /*
    Catálogo de tipos de benefício (card #188). Carregado uma vez por página,
    pelo mesmo motivo do catálogo de cláusulas: é uma lista pequena, que muda
    raramente, e recarregá-la a cada "Novo anúncio" só atrasaria o formulário.
  */
  const benefitTypesState = useAsync(() => adminApi.listBenefitTypes(), []);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<PromotionDto | null>(null);
  const [form, setForm] = useState<PromotionRequestDto>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState<PromotionDto | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [ruleDraft, setRuleDraft] = useState<PromotionRuleDraft[]>([]);
  /**
   * Se o lojista mexeu nas condições nesta abertura do modal. Só importa na
   * criação: é o que separa "não toquei no campo" (deixa o core aplicar as
   * cláusulas padrão) de "quero este anúncio sem condição nenhuma".
   */
  const [rulesTouched, setRulesTouched] = useState(false);
  /**
   * O benefício em edição. `undefined` = "sem benefício configurado", que é o
   * estado dos anúncios anteriores ao motor e continua sendo um estado válido.
   */
  const [benefitDraft, setBenefitDraft] = useState<PromotionBenefitRequestDto | undefined>(undefined);
  /**
   * Se o lojista encostou no benefício nesta abertura do modal. Mesma razão de
   * `rulesTouched`: `benefit: undefined` no payload significa "não mexi", e sem
   * este sinal a edição de um anúncio cujo formulário nem abriu a seção
   * apagaria o benefício existente.
   */
  const [benefitTouched, setBenefitTouched] = useState(false);

  const rows = data?.content ?? [];
  const summary = summaryState.data;
  const ruleTemplates = ruleTemplatesState.data ?? [];
  const benefitTypes = benefitTypesState.data ?? [];
  const ruleErrors = useMemo(() => validatePromotionRules(ruleDraft), [ruleDraft]);

  const reloadAll = () => {
    reload();
    summaryState.reload();
  };

  const handleSearchChange = (value: string) => {
    setSearch(value);
    setPage(0);
  };

  const handleStatusChange = (value: string) => {
    setStatusFilter(value);
    setPage(0);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setPage(0);
  };

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    // Anúncio novo abre com tudo desmarcado: o endpoint do lojista devolve só
    // `id` e `text`, então a tela não tem como saber quais cláusulas são padrão.
    setRuleDraft([]);
    setRulesTouched(false);
    setBenefitDraft(undefined);
    setBenefitTouched(false);
    setModalOpen(true);
  };

  const openEdit = (promotion: PromotionDto) => {
    setEditing(promotion);
    setForm({
      title: promotion.title,
      description: promotion.description,
      type: promotion.type,
      discountPercent: promotion.discountPercent,
      cashbackPercent: promotion.cashbackPercent,
      imageUrl: promotion.imageUrl,
      highlighted: promotion.highlighted,
      startsAt: promotion.startsAt.slice(0, 10),
      endsAt: promotion.endsAt.slice(0, 10),
      maxRedemptions: promotion.maxRedemptions,
      /*
        As fotos entram no FORMULÁRIO, e não num estado à parte, justamente
        para que todo salvamento as reenvie. Ver o comentário de `handleSubmit`:
        `galleryUrls` ausente no `PUT` não é "não mexi", é lista vazia - e apaga
        a galeria do anúncio. Anúncio de backend antigo chega sem o campo.
      */
      galleryUrls: promotion.galleryUrls ?? [],
    });
    setRuleDraft(promotionRulesToDraft(promotion.rules));
    setRulesTouched(false);
    setBenefitDraft(benefitToDraft(promotion.benefit));
    setBenefitTouched(false);
    setModalOpen(true);
  };

  const update = <K extends keyof PromotionRequestDto>(key: K, value: PromotionRequestDto[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const handleSubmit = async () => {
    if (hasPromotionRuleErrors(ruleErrors)) {
      showNotification('Revise as condições do anúncio antes de salvar.', 'error');
      return;
    }

    const rulesPayload = promotionRulesToPayload(ruleDraft, ruleTemplates);
    const payload: PromotionRequestDto = {
      /*
        O espalhamento do formulário é o que leva `galleryUrls` em TODO
        salvamento - inclusive o de quem só corrigiu o título.

        Não é zelo: `MerchantPromotionRequestDto.galleryUrls` é `List<String>`
        não anulável com `= emptyList()` no core, então um `PUT` sem o campo
        chega como lista vazia e o `PromotionGalleryService.replace` APAGA as
        fotos do anúncio. É o oposto de `rules` e `benefit` logo abaixo, onde
        `undefined` quer dizer "não mexi" - e é por isso que o tipo do campo é
        obrigatório: quem esquecer não compila. Há teste prendendo isto
        (`PromotionsPage.test.tsx`).
      */
      ...form,
      /*
        `rules: undefined` diz ao core "não mexa nas condições" - e, na criação,
        faz o anúncio nascer com as cláusulas marcadas como padrão no catálogo.
        Agora que o formulário desenha o campo, a edição SEMPRE manda uma lista
        explícita (vazia inclusive): sem isso o lojista desmarcaria tudo, salvaria
        e veria as padrões voltarem. Na criação, `undefined` fica reservado para o
        caso em que ele não encostou no campo, que é justamente onde as padrões
        são o comportamento desejado.
      */
      rules: editing || rulesTouched ? rulesPayload : undefined,
      /*
        Mesma convenção: `undefined` diz ao core "não mexa no benefício". Na
        criação, `undefined` deixa o anúncio sem benefício configurado - e o
        core NÃO inventa um padrão, porque escolher o benefício por conta
        própria seria a plataforma decidindo o que a loja está oferecendo.
      */
      benefit: benefitTouched ? benefitDraft : undefined,
    };

    setSaving(true);
    try {
      if (editing) {
        await adminApi.updatePromotion(editing.id, payload);
        showNotification('Anúncio atualizado com sucesso.', 'success');
      } else {
        await adminApi.createPromotion(payload);
        showNotification('Anúncio criado com sucesso.', 'success');
      }
      setModalOpen(false);
      reloadAll();
    } catch (err) {
      /*
        O teto de 10 fotos é conferido nas DUAS pontas, e as duas são
        necessárias: a tela não deixa chegar na 11ª (é o que o lojista sente),
        e o core recusa de todo jeito (é o que vale). Sem este mapa a recusa do
        servidor chegaria como a frase crua dele; com ele o lojista lê o que
        fazer a respeito.
      */
      showNotification(
        extractErrorMessage(err, 'Não foi possível salvar o anúncio.', PROMOTION_GALLERY_ERROR_MESSAGES),
        'error'
      );
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = async (promotion: PromotionDto) => {
    const action = promotion.status === 'ACTIVE' ? 'pause' : 'activate';
    try {
      await adminApi.setPromotionStatus(promotion.id, action);
      showNotification(action === 'pause' ? 'Anúncio pausado.' : 'Anúncio ativado.', 'success');
      reloadAll();
    } catch (err) {
      showNotification(extractErrorMessage(err, 'Não foi possível alterar o status.'), 'error');
    }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    setDeleteLoading(true);
    try {
      await adminApi.deletePromotion(deleting.id);
      showNotification('Anúncio removido.', 'success');
      setDeleting(null);
      reloadAll();
    } catch (err) {
      showNotification(extractErrorMessage(err, 'Não foi possível remover o anúncio.'), 'error');
    } finally {
      setDeleteLoading(false);
    }
  };

  const totalPages = data?.totalPages ?? 0;
  const totalItems = data?.totalElements;

  return (
    <>
      <PageHeader
        title="Anúncios"
        subtitle="Crie e gerencie as promoções da sua loja no Kardume Urbano."
        actions={
          <Button variant="primary" onClick={openCreate}>
            <Plus size={16} /> Novo anúncio
          </Button>
        }
      />

      <StatsGrid>
        <StatCard
          label="Total de anúncios"
          value={formatNumber(summary?.total ?? 0)}
          icon={<Megaphone size={18} />}
        />
        <StatCard
          label="Ativos"
          value={formatNumber(summary?.active ?? 0)}
          icon={<CheckCircle2 size={18} />}
          iconColor={theme.success}
        />
        <StatCard
          label="Em análise"
          value={formatNumber(summary?.pendingReview ?? 0)}
          icon={<Clock size={18} />}
          iconColor={theme.warning}
        />
        <StatCard
          label="Resgates"
          value={formatNumber(summary?.totalRedemptions ?? 0)}
          icon={<Ticket size={18} />}
          iconColor={theme.info}
        />
      </StatsGrid>

      <Toolbar>
        <SearchInput
          placeholder="Buscar por título..."
          value={search}
          onChange={(e) => handleSearchChange(e.target.value)}
        />
        <Select
          options={STATUS_FILTER_OPTIONS}
          value={statusFilter}
          onChange={(e) => handleStatusChange(e.target.value)}
        />
        <ActionButton title="Atualizar" onClick={reloadAll}>
          <RefreshCw size={16} />
        </ActionButton>
      </Toolbar>

      <DataState
        loading={loading}
        error={error}
        isEmpty={rows.length === 0}
        emptyTitle="Nenhum anúncio encontrado"
        emptyDescription="Ajuste os filtros ou crie seu primeiro anúncio para atrair clientes."
        onRetry={reloadAll}
      >
        <TableContainer>
          <Table>
            <thead>
              <tr>
                <th>Capa</th>
                <th>Título</th>
                <th>Tipo</th>
                <th>Status</th>
                <th>Visualizações</th>
                <th>Resgates</th>
                <th>Período</th>
                <th>Destaque</th>
                <th style={{ textAlign: 'right' }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((promotion) => (
                <tr key={promotion.id}>
                  <td>
                    <Thumbnail src={promotion.imageUrl} alt={promotion.title} />
                  </td>
                  <td>{promotion.title}</td>
                  <td>{PROMOTION_TYPE_LABELS[promotion.type]}</td>
                  <td>
                    <Badge tone={statusTone(promotion.status)}>
                      {PROMOTION_STATUS_LABELS[promotion.status]}
                    </Badge>
                  </td>
                  <td>{formatNumber(promotion.views)}</td>
                  <td>{formatNumber(promotion.redemptions)}</td>
                  <td>
                    {formatDate(promotion.startsAt)} – {formatDate(promotion.endsAt)}
                  </td>
                  <td>
                    {promotion.highlighted ? (
                      <Star size={16} color={theme.warning} fill={theme.warning} />
                    ) : (
                      <span style={{ color: theme.textMuted }}>-</span>
                    )}
                  </td>
                  <td>
                    <Actions>
                      <ActionButton title="Editar" onClick={() => openEdit(promotion)}>
                        <Edit3 size={16} />
                      </ActionButton>
                      <ActionButton
                        title={promotion.status === 'ACTIVE' ? 'Pausar' : 'Ativar'}
                        onClick={() => handleToggleStatus(promotion)}
                      >
                        {promotion.status === 'ACTIVE' ? <Pause size={16} /> : <Play size={16} />}
                      </ActionButton>
                      <ActionButton
                        title="Remover"
                        color={theme.error}
                        onClick={() => setDeleting(promotion)}
                      >
                        <Trash2 size={16} />
                      </ActionButton>
                    </Actions>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableContainer>
      </DataState>

      {!error && (
        <Pagination
          currentPage={page + 1}
          totalPages={totalPages}
          totalItems={totalItems}
          pageSize={pageSize}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          disabled={loading}
          onPageChange={(next) => setPage(next - 1)}
          onPageSizeChange={handlePageSizeChange}
        />
      )}

      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? 'Editar anúncio' : 'Novo anúncio'}
        width="640px"
        footer={
          <>
            <Button variant="ghost" onClick={() => setModalOpen(false)}>
              Cancelar
            </Button>
            <Button variant="primary" isLoading={saving} onClick={handleSubmit}>
              {editing ? 'Salvar alterações' : 'Criar anúncio'}
            </Button>
          </>
        }
      >
        <FormGrid>
          <FullRow>
            <TextInput
              label="Título"
              value={form.title}
              onChange={(e) => update('title', e.target.value)}
              placeholder="Ex.: 20% de desconto no almoço"
            />
          </FullRow>
          <FullRow>
            <RichTextInput
              label="Descrição"
              value={form.description}
              onChange={(html) => update('description', html)}
              placeholder="Detalhe as condições do anúncio"
            />
          </FullRow>
          <FullRow>
            {/*
              Upload target is GALLERY, not COVER: COVER is the *store* banner
              edited in "Perfil da loja". Sharing that target would mix the
              store cover with every ad cover in the same slot.
            */}
            <ImageUpload
              label="Imagem de capa"
              variant="cover"
              value={form.imageUrl}
              onChange={(url) => update('imageUrl', url)}
              onUpload={uploadMerchantImage('GALLERY')}
              onError={(message) => showNotification(message, 'error')}
              helpText="Proporção 16:9 (ex.: 1280x720). É a imagem que aparece no app e no site."
            />
          </FullRow>
          <FullRow>
            <FieldGroupLabel>Fotos do anúncio</FieldGroupLabel>
            <FieldGroupHint>
              O carrossel que o cliente folheia antes de decidir. A primeira foto é a que abre a
              página e a que aparece quando alguém compartilha o anúncio. Sem foto aqui, o anúncio
              mostra a imagem de capa acima.
            </FieldGroupHint>
            {/*
              O MESMO componente da galeria da loja e o MESMO caminho de upload
              da capa logo acima (`uploadMerchantImage('GALLERY')`, alvo GALLERY
              e não COVER - ver o comentário lá). Um segundo componente de
              galeria, ou um alvo de upload novo, seria dívida nascida pronta:
              a spec 034 §7 pede semelhança em letra, e o caminho único de
              upload é critério de aceite do card.
            */}
            <GalleryInput
              value={form.galleryUrls}
              onChange={(urls) => update('galleryUrls', urls)}
              onUpload={uploadMerchantImage('GALLERY')}
              onError={(message) => showNotification(message, 'error')}
              max={PROMOTION_GALLERY_MAX_COUNT}
              disabled={saving}
              helpText={`Até ${PROMOTION_GALLERY_MAX_COUNT} fotos, 5 MB cada (JPG, PNG, GIF ou WebP). ${form.galleryUrls.length}/${PROMOTION_GALLERY_MAX_COUNT} usadas.`}
            />
          </FullRow>
          <Select
            label="Tipo"
            options={TYPE_OPTIONS}
            value={form.type}
            onChange={(e) => update('type', e.target.value as PromotionType)}
          />
          {form.type === 'DISCOUNT' && (
            <TextInput
              label="Desconto (%)"
              type="number"
              value={form.discountPercent ?? ''}
              onChange={(e) =>
                update('discountPercent', e.target.value === '' ? undefined : Number(e.target.value))
              }
            />
          )}
          {form.type === 'CASHBACK' && (
            <TextInput
              label="Cashback (%)"
              type="number"
              value={form.cashbackPercent ?? ''}
              onChange={(e) =>
                update('cashbackPercent', e.target.value === '' ? undefined : Number(e.target.value))
              }
            />
          )}
          <TextInput
            label="Início"
            type="date"
            value={form.startsAt}
            onChange={(e) => update('startsAt', e.target.value)}
          />
          <TextInput
            label="Término"
            type="date"
            value={form.endsAt}
            onChange={(e) => update('endsAt', e.target.value)}
          />
          <TextInput
            label="Limite de resgates"
            type="number"
            value={form.maxRedemptions ?? ''}
            onChange={(e) =>
              update('maxRedemptions', e.target.value === '' ? undefined : Number(e.target.value))
            }
          />
          <FullRow>
            <FieldGroupLabel>Benefício</FieldGroupLabel>
            <FieldGroupHint>
              O que o cliente ganha, e para quem. É isto que o app mostra e que o balcão precisa
              entregar - o texto abaixo continua sendo só a descrição.
            </FieldGroupHint>
            <BenefitInput
              value={benefitDraft}
              types={benefitTypes}
              typesLoading={benefitTypesState.loading}
              typesError={benefitTypesState.error}
              disabled={saving}
              onChange={(next) => {
                setBenefitDraft(next);
                setBenefitTouched(true);
              }}
            />
          </FullRow>
          <FullRow>
            <FieldGroupLabel>Condições do anúncio</FieldGroupLabel>
            <FieldGroupHint>
              São as regras que o cliente lê antes de resgatar, na ordem mostrada aqui. Marque as
              cláusulas da plataforma e acrescente as suas, se precisar.
            </FieldGroupHint>
            <PromotionRulesInput
              mode={editing ? 'edit' : 'create'}
              value={ruleDraft}
              touched={rulesTouched}
              templates={ruleTemplates}
              templatesLoading={ruleTemplatesState.loading}
              templatesError={ruleTemplatesState.error}
              disabled={saving}
              onChange={(rules, meta) => {
                setRuleDraft(rules);
                if (meta.touched) setRulesTouched(true);
              }}
            />
          </FullRow>
          <FullRow>
            <SwitchRow>
              <Switch
                checked={!!form.highlighted}
                onChange={(value) => update('highlighted', value)}
              />
              Destacar este anúncio na vitrine
            </SwitchRow>
          </FullRow>
        </FormGrid>
      </Modal>

      <ConfirmModal
        isOpen={!!deleting}
        danger
        title="Remover anúncio"
        message={
          <>
            Tem certeza que deseja remover o anúncio <strong>{deleting?.title}</strong>? Esta ação não pode ser
            desfeita.
          </>
        }
        confirmLabel="Remover"
        loading={deleteLoading}
        onConfirm={confirmDelete}
        onClose={() => setDeleting(null)}
      />
    </>
  );
};

export default PromotionsPage;
